import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { ConversationLoader } from "../../components/OlliLogo";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Showcase, setupModules } from "../../components/auth/Showcase";
import { Stepper } from "../../components/auth/Stepper";
import { AUTH_PATHS, firstName, useSignup } from "../../components/auth/signupState";
import { PrimaryButton } from "../../components/ui";

/**
 * Account setup (design: screens/Setup.tsx). No API: by the time this shows,
 * POST /api/auth/verify-otp has already created the account, the organisation,
 * its lead stages and the free plan. The checklist is the design's timed
 * animation, then "Go to dashboard" plays the Welcome splash.
 */
export default function Setup() {
  const { data } = useSignup();
  const navigate = useNavigate();
  const clinic = data.orgName.trim() || "your clinic";
  const tasks = setupModules.map((m) => m.task);
  const [done, setDone] = useState(0);

  useEffect(() => {
    if (done >= tasks.length) return undefined;
    const t = setTimeout(() => setDone((d) => d + 1), 900);
    return () => clearTimeout(t);
  }, [done, tasks.length]);

  const ready = done >= tasks.length;
  const name = firstName(data.name);

  return (
    <AuthLayout aside={<Showcase step="setup" data={data} done={done} />}>
      <div className="flex flex-col gap-8">
        <Stepper current="setup" />
        {ready ? (
          <div key="ready" className="flex animate-fade-in flex-col gap-8">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sun">
              <Check size={26} strokeWidth={2} />
            </span>
            <div className="flex flex-col gap-2">
              <h1 className="text-h1 font-semibold">{clinic === "your clinic" ? "Your account is ready" : `${clinic} is ready`}</h1>
              <p className="text-body text-muted">
                {name ? `Welcome to Olli, ${name}. ` : ""}Next, set up your front desk and connect WhatsApp.
              </p>
            </div>
            <PrimaryButton onClick={() => navigate(AUTH_PATHS.welcome, { replace: true })}>
              Go to dashboard
              <ArrowRight size={18} strokeWidth={1.75} />
            </PrimaryButton>
          </div>
        ) : (
          <div key="working" className="flex animate-fade-in flex-col gap-8">
            <div className="flex flex-col gap-2">
              <h1 className="text-h1 font-semibold">Setting up your account</h1>
              <p className="text-body text-muted">This takes a few seconds</p>
            </div>
            <ul className="flex flex-col gap-4" aria-live="polite">
              {tasks.map((t, i) => {
                const state = i < done ? "done" : i === done ? "active" : "todo";
                return (
                  <li key={t} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                      {state === "done" ? (
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-paper">
                          <Check size={16} strokeWidth={2.25} />
                        </span>
                      ) : state === "active" ? (
                        <ConversationLoader size={28} tone="white" />
                      ) : (
                        <span className="h-7 w-7 rounded-full border-2 border-line" />
                      )}
                    </span>
                    <span className={state === "todo" ? "text-body text-muted" : "text-body font-medium text-ink"}>
                      {t}
                      {state === "active" ? "…" : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
